import React, { useEffect, useState } from 'react';
import { Box, Drawer, Typography, IconButton, InputAdornment } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';

import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import { couponFormSchema } from 'utils/schema/validator/CouponValidation';
import { useCreateCoupon, useUpdateCoupon, useGetCoupon, useGetCouponCode } from 'apis/agency/coupons/hook';
import CouponFormSkeleton from './CouponFormSkeleton';
import DateRangePicker from '@/components/Common/DateRangePicker';
import { useGetBusinesses } from '@/apis/agency/hook';

export default function CouponForm() {
  const { couponId } = useParams();
  const navigate = useNavigate();
  const [generateCode, setGenerateCode] = useState(false);

  const { data: coupon, isLoading: loadingCouponData } = useGetCoupon();
  const defaultBusinessIds = coupon?.specific_resources?.map((resource: any) => resource.id) || [];
  const { data: businesses, isLoading: loadingBusinesses } = useGetBusinesses({
    per_page: 9999,
    filter: { active_and_past_due: true }
  });

  const { data: couponCode, isLoading: loadingCouponCode } = useGetCouponCode(generateCode);

  const { handleSubmit, control, watch, setValue } = useForm({
    resolver: zodResolver(couponFormSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      code: '',
      coupon_type: 'generic',
      discount_type: 'percentage',
      discount_value: '',
      total_amount: '',
      date_range: '',
      validity_period: '',
      specific_resource_ids: []
    },
    values: coupon
      ? {
          ...coupon,
          specific_resource_ids: defaultBusinessIds,
          date_range: `${coupon?.starts_at},${coupon?.expires_at}`
        }
      : undefined
  });
  const currCouponType = watch('coupon_type');
  const currDiscountType = watch('discount_type');
  const { mutate: createCoupon, isPending: isCreating } = useCreateCoupon();
  const { mutate: updateCoupon, isPending: isUpdating } = useUpdateCoupon();

  const onSubmit = (data: any) => {
    if (data.date_range) {
      const dateArr = data.date_range?.split(',');
      data = { ...data, starts_at: dateArr[0], expires_at: dateArr[1] };
    }
    if (data.discount_type !== 'percentage') {
      data = { ...data, total_amount: null };
    }
    if (couponId) {
      updateCoupon({ id: Number(couponId), ...data });
    } else {
      createCoupon(data);
    }

    navigate(-1);
  };

  const couponTypeOptions = [
    { value: 'generic', label: 'Generic' },
    { value: 'business_specific', label: 'Business Specific' }
  ];

  const discountTypeOptions = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed_amount', label: 'Fixed Amount' }
  ];

  const watchedName = watch('name');
  const isLoading = loadingCouponData;

  useEffect(() => {
    if (couponCode?.code) setValue('code', couponCode?.code);
    return () => {
      setGenerateCode(false);
    };
  }, [couponCode?.code, setValue]);

  useEffect(() => {
    if (!couponId) {
      setValue('code', '');
    }
  }, [couponId, setValue]);

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001,
          overflow: 'visible'
        }
      }}
    >
      {isLoading ? (
        <CouponFormSkeleton />
      ) : (
        <>
          {/* Fixed Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid #DEDEDE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            <Typography variant='h6'>{couponId ? 'Update' : 'Create New'} Coupon</Typography>
            <IconButton onClick={() => navigate(-1)} size='small'>
              <IoClose size={24} />
            </IconButton>
          </Box>

          {/* Form with Fixed Footer */}
          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            {/* Scrollable Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 3,
                position: 'relative',
                zIndex: 1
              }}
            >
              <Grid container spacing={3}>
                {/* Name */}
                <Grid size={12}>
                  <Input
                    name='name'
                    label='Coupon Name'
                    control={control}
                    fullWidth
                    required
                    placeholder='Type here'
                    helperText={`${watchedName?.length || 0}/25 Characters`}
                  />
                </Grid>

                {/* Coupon Code */}
                <Grid size={12}>
                  <Input
                    name='code'
                    label='Coupon Code'
                    control={control}
                    fullWidth
                    disabled={!!couponId || loadingCouponCode}
                    required
                    placeholder='Enter Coupon Code'
                    slotProps={
                      !couponId
                        ? {
                            input: {
                              endAdornment: (
                                <InputAdornment position='end'>
                                  <Typography
                                    variant='body2'
                                    color='primary'
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => setGenerateCode(true)}
                                  >
                                    Get Code
                                  </Typography>
                                </InputAdornment>
                              )
                            }
                          }
                        : {}
                    }
                  />
                </Grid>

                {/* Coupon Type */}
                <Grid size={12}>
                  <Select
                    name='coupon_type'
                    label='Coupon Type'
                    control={control}
                    disabled={!!couponId}
                    options={couponTypeOptions}
                  />
                </Grid>

                {currCouponType === 'business_specific' && (
                  <Grid size={12}>
                    <Select
                      name='specific_resource_ids'
                      disabled={loadingBusinesses || !!couponId}
                      control={control}
                      label='Select Businesses'
                      multiple
                      required
                      options={
                        businesses?.data?.map((business) => ({ label: business.name, value: business.id })) ?? []
                      }
                    />
                  </Grid>
                )}

                {/* Discount Section */}
                <Grid size={6}>
                  <Input
                    name='discount_value'
                    label='Discount'
                    control={control}
                    disabled={!!couponId}
                    fullWidth
                    required
                    type='number'
                    placeholder='Enter Discount Value'
                  />
                </Grid>
                <Grid size={6}>
                  <Select
                    name='discount_type'
                    label='Type'
                    control={control}
                    disabled={!!couponId}
                    options={discountTypeOptions}
                  />
                </Grid>

                {currDiscountType === 'percentage' && (
                  <Grid size={12}>
                    <Input
                      name='total_amount'
                      label='Total amount'
                      control={control}
                      disabled={!!couponId}
                      fullWidth
                      required
                      type='number'
                      placeholder='0'
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Typography variant='body2' color='text.secondary'>
                                $
                              </Typography>
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                  </Grid>
                )}

                {/* Apply By Date */}
                <Grid size={12}>
                  <Box sx={{ width: '100%' }}>
                    <DateRangePicker
                      label='Apply By Date'
                      disabled={!!couponId}
                      name='date_range'
                      control={control}
                      fullWidth
                      sx={{ height: '56px' }}
                    />
                  </Box>
                  <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                    This coupon will expire after this period.
                  </Typography>
                </Grid>

                {/* Validity Period */}
                <Grid size={12}>
                  <Input
                    name='validity_period'
                    label='Validity Period'
                    disabled={!!couponId}
                    control={control}
                    fullWidth
                    type='number'
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <Typography variant='caption' color='text.secondary'>
                              Months
                            </Typography>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Fixed Footer */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid #DEDEDE',
                display: 'flex',
                justifyContent: 'end',
                gap: '16px',
                flexShrink: 0
              }}
            >
              <Button
                color='secondary'
                variant='outlined'
                sx={{ border: '1px solid #F3F3F3' }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained' isPending={isCreating || isUpdating}>
                {couponId ? 'Save' : 'Create'}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
}
